"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/common/Reveal";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface ProductCardItem {
  id: string;
  title: Record<Lang, string>;
  badge: Record<Lang, string>;
  description: Record<Lang, string>;
  href: string;
  specs: Record<Lang, string>;
  image: string;
  imgAlt: Record<Lang, string>;
}
const recommendedProducts: ProductCardItem[] = [{
  id: "plain-dutch",
  title: {
    en: "Plain Dutch Weave",
    zh: "平纹荷兰编织丝网",
    es: "Tejido Dutch liso",
    de: "Glatte Tresse",
    fr: "Tissage hollandais uni",
    pt: "Trama holandesa lisa"
  },
  badge: {
    en: "10 µm – 100 µm",
    zh: "10 µm – 100 µm",
    es: "10 µm – 100 µm",
    de: "10 µm – 100 µm",
    fr: "10 µm – 100 µm",
    pt: "10 µm – 100 µm"
  },
  description: {
    en: "Dense surface weave for sugar syrup clarification, beverage straining, and sanitary pressure leaf filtration.",
    zh: "致密表面编织结构，适用于糖浆澄清、饮料过滤及卫生级叶滤机固液分离。",
    es: "Tejido de superficie densa para clarificación de jarabes, bebidas y filtración en filtros de hojas a presión.",
    de: "Dichtes Oberflächengewebe für Sirupklärung, Getränkefiltration und Druckblattfiltration.",
    fr: "Tissage de surface dense pour clarification de sirops, boissons et filtration sur filtres à feuilles.",
    pt: "Trama de superfície densa para clarificação de xaropes, bebidas e filtração sob pressão."
  },
  href: "/PlainDutchWeavePage",
  specs: {
    en: "Liquid Clarification",
    zh: "液体精密澄清",
    es: "Clarificación de líquidos",
    de: "Flüssigkeitsklärung",
    fr: "Clarification de liquides",
    pt: "Clarificação de líquidos"
  },
  image: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
  imgAlt: {
    en: "Plain Dutch weave stainless steel wire cloth for sanitary liquid clarification",
    zh: "用于卫生级液体澄清的平纹荷兰编织不锈钢丝布",
    es: "Malla de alambre de tejido Dutch liso de acero inoxidable para clarificación sanitaria de líquidos",
    de: "Glatte Tresse Edelstahldrahtgewebe für sanitäre Flüssigkeitsklärung",
    fr: "Toile métallique inox tissage hollandais uni pour clarification sanitaire de liquides",
    pt: "Tela de arame inox de trama holandesa lisa para clarificação sanitária de líquidos"
  }
}, {
  id: "twill-dutch",
  title: {
    en: "Twilled Dutch Weave",
    zh: "斜纹荷兰编织丝网",
    es: "Tejido Dutch cruzado",
    de: "Köpertresse",
    fr: "Tissage hollandais sergé",
    pt: "Trama holandesa sarja"
  },
  badge: {
    en: "1 µm – 25 µm",
    zh: "1 µm – 25 µm",
    es: "1 µm – 25 µm",
    de: "1 µm – 25 µm",
    fr: "1 µm – 25 µm",
    pt: "1 µm – 25 µm"
  },
  description: {
    en: "Fine micron ratings down to 1 µm with high mechanical strength for active pharmaceutical ingredient polishing.",
    zh: "精细至 1 µm 拦截精度与高机械抗压强度，适用于原料药（API）精滤与无菌流体处理。",
    es: "Clasificación fina hasta 1 µm con alta resistencia mecánica para pulido de principios activos farmacéuticos.",
    de: "Feine Filterfeinheit bis 1 µm bei hoher mechanischer Festigkeit für die Politur pharmazeutischer Wirkstoffe.",
    fr: "Finesse de filtration jusqu'à 1 µm et haute résistance mécanique pour la clarification de principes actifs.",
    pt: "Retenção fina até 1 µm com alta resistência mecânica para polimento de princípios ativos farmacêuticos."
  },
  href: "/TwillDutchWeavePage",
  specs: {
    en: "Fine API Polishing",
    zh: "原料药精制精滤",
    es: "Pulido fino de API",
    de: "Feine API-Politur",
    fr: "Finition fine d'API",
    pt: "Polimento fino de API"
  },
  image: "https://mutualfilter.com/wp-content/uploads/2026/09/twill-dutch-wire-mesh.webp",
  imgAlt: {
    en: "Twilled Dutch weave stainless steel wire cloth for pharmaceutical polishing",
    zh: "用于制药精制过滤的斜纹荷兰编织不锈钢丝布",
    es: "Malla de alambre tejida Dutch cruzada de acero inoxidable para pulido farmacéutico",
    de: "Köpertressedrahtgewebe aus Edelstahl für pharmazeutische Feinfiltration",
    fr: "Toile métallique tissage hollandais sergé inox pour filtration pharmaceutique fine",
    pt: "Malha de trama holandesa sarja em aço inoxidável para polimento farmacêutico"
  }
}, {
  id: "multilayer",
  title: {
    en: "Multilayer Sintered Mesh",
    zh: "多层复合烧结网",
    es: "Malla sinterizada multicapa",
    de: "Mehrlagiges Sintergewebe",
    fr: "Toile frittée multicouche",
    pt: "Malha sinterizada multicamadas"
  },
  badge: {
    en: "2 µm – 200 µm",
    zh: "2 µm – 200 µm",
    es: "2 µm – 200 µm",
    de: "2 µm – 200 µm",
    fr: "2 µm – 200 µm",
    pt: "2 µm – 200 µm"
  },
  description: {
    en: "Diffusion-bonded 5-layer laminate providing rigid non-shedding structure for Nutsche filters and centrifuge baskets.",
    zh: "扩散粘结 5 层复合板结构，提供高刚性无脱落滤网，适用于三合一过滤洗涤干燥机与离心机筛篮。",
    es: "Laminado sinterizado de 5 capas unido por difusión para filtros Nutsche y cestas centrífugas sin desprendimiento.",
    de: "Diffusionsgebundenes 5-Lagen-Laminat als formstabile, abriebfreie Struktur für Nutschenfilter und Zentrifugenkörbe.",
    fr: "Laminé 5 couches soudé par diffusion offrant une structure rigide sans relargage pour filtres Nutsche et centrifugeuses.",
    pt: "Laminado de 5 camadas unido por difusão fornecendo estrutura rígida sem desprendimento para filtros Nutsche e centrífugas."
  },
  href: "/SinteredMultilayerMeshPage",
  specs: {
    en: "Nutsche Filter Plates",
    zh: "过滤洗涤干燥底板",
    es: "Placas de filtro Nutsche",
    de: "Nutschenfilter-Platten",
    fr: "Plaques filtrantes Nutsche",
    pt: "Placas de filtro Nutsche"
  },
  image: "https://mutualfilter.com/wp-content/uploads/2026/09/sintered-mesh-5layer-disc.webp",
  imgAlt: {
    en: "Multilayer sintered wire mesh laminate disc for pharmaceutical Nutsche filter dryers",
    zh: "用于制药过滤洗涤干燥机的多层烧结不锈钢网板",
    es: "Disco laminado de malla sinterizada multicapa para secadores de filtro Nutsche farmacéuticos",
    de: "Mehrlagige Sinterscheibe für pharmazeutische Nutschenfiltertrockner",
    fr: "Disque laminé fritté multicouche pour sécheurs-filtres Nutsche pharmaceutiques",
    pt: "Disco laminado sinterizado multicamadas para secadores de filtro Nutsche farmacêuticos"
  }
}, {
  id: "cartridges",
  title: {
    en: "Filter Cartridges",
    zh: "卫生级金属滤芯与滤筒",
    es: "Cartuchos filtrantes",
    de: "Filterkerzen",
    fr: "Cartouches filtrantes",
    pt: "Cartuchos filtrantes"
  },
  badge: {
    en: "Cylindrical & Pleated",
    zh: "圆筒与折波结构",
    es: "Cilíndricos y plisados",
    de: "Zylindrisch & plissiert",
    fr: "Cylindriques et plissées",
    pt: "Cilíndricos e plissados"
  },
  description: {
    en: "All-metal sanitary welded cartridges designed for sterile vent housings, steam filtration, and CIP backwashing.",
    zh: "全金属卫生级焊接滤芯，专用于储罐呼吸器无菌排气、蒸汽灭菌精滤与在线反洗管路。",
    es: "Cartuchos sanitarios totalmente soldados diseñados para venteos estériles, filtración de vapor y retrolavado CIP.",
    de: "Vollverschweißte Sanitär-Filterkerzen für sterile Belüftungsgehäuse, Dampffiltration und CIP-Rückspülung.",
    fr: "Cartouches sanitaires entièrement soudées pour évents stériles, filtration de vapeur et rétro-lavage NEP.",
    pt: "Cartuchos sanitários totalmente soldados projetados para respiros estéreis, filtração de vapor e retrolavagem CIP."
  },
  href: "/FilterCartridgesPage",
  specs: {
    en: "Sanitary Housings",
    zh: "卫生级外壳与接口",
    es: "Carcasas sanitarias",
    de: "Sanitärgehäuse",
    fr: "Corps de filtres sanitaires",
    pt: "Carcaças sanitárias"
  },
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/pharma_sterile_gas_filter.png",
  imgAlt: {
    en: "All-metal sanitary filter cartridge for pharmaceutical steam and liquid filtration",
    zh: "用于制药蒸汽与卫生液体过滤的全金属滤芯",
    es: "Cartucho filtrante sanitario metálico para vapor y líquidos farmacéuticos",
    de: "Ganzmetall-Sanitärfilterkerze für pharmazeutischen Dampf und Flüssigkeiten",
    fr: "Cartouche filtrante sanitaire tout métal pour vapeur et fluides pharmaceutiques",
    pt: "Cartucho filtrante sanitário metálico para vapor e líquidos farmacêuticos"
  }
}];
interface Copy {
  eyebrow: string;
  heading: string;
  lead: string;
  exploreLink: string;
}
const copyData: Record<Lang, Copy> = {
  en: {
    eyebrow: "Product Configurations",
    heading: "Recommended Products for Food & Pharmaceutical Filtration",
    lead: "Engineered sanitary mesh elements tailored for exact particle retention, CIP cleanability, and non-shedding process integrity.",
    exploreLink: "View Specification"
  },
  zh: {
    eyebrow: "适配产品配置",
    heading: "食品与制药过滤推荐产品",
    lead: "专为严苛微米级颗粒截留、CIP 在位清洗及零介质脱落卫生完整性定制的金属丝网元件。",
    exploreLink: "查看规格参数"
  },
  es: {
    eyebrow: "Configuraciones de producto",
    heading: "Productos recomendados para filtración de alimentos y farmacia",
    lead: "Elementos de malla sanitaria diseñados para retención exacta, lavabilidad CIP e integridad de proceso sin desprendimiento.",
    exploreLink: "Ver especificación"
  },
  de: {
    eyebrow: "Produktkonfigurationen",
    heading: "Empfohlene Produkte für die Lebensmittel- und Pharmafiltration",
    lead: "Sanitäre Gewebekomponenten für exakte Partikelrückhaltung, CIP-Reinigbarkeit und abriebfreie Prozessintegrität.",
    exploreLink: "Spezifikation ansehen"
  },
  fr: {
    eyebrow: "Configurations produits",
    heading: "Produits recommandés pour la filtration agroalimentaire et pharmaceutique",
    lead: "Éléments filtrants sanitaires conçus pour une rétention précise, la nettoyabilité NEP et l'intégrité sans relargage.",
    exploreLink: "Voir les spécifications"
  },
  pt: {
    eyebrow: "Configurações de produtos",
    heading: "Produtos recomendados para filtração de alimentos e farmacêutica",
    lead: "Elementos de tela sanitária desenvolvidos para retenção precisa, limpabilidade CIP e integridade sem desprendimento.",
    exploreLink: "Ver especificação"
  }
};
export default function RecommendedProducts() {
  const {
    lang
  } = useLanguage();
  const c = pick(lang, copyData);
  return <section id="recommendedproducts" data-nav="light" className="bg-background mf-section border-b border-border text-foreground">
      <div className="mf-container">
        {/* Section Heading & Short Lead */}
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <span className="text-sm font-mono uppercase tracking-wider text-muted-foreground font-semibold block mb-2">
                {c.eyebrow}
              </span>
              <h2 className="font-bold text-foreground font-default tracking-tight mf-h2">
                {c.heading}
              </h2>
            </div>
            <p className="text-muted-foreground max-w-md mf-lead">
              {c.lead}
            </p>
          </div>
        </Reveal>

        {/* 4 Recommended Product Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {recommendedProducts.map((item, index) => <Reveal key={item.id} delay={Math.min(index * 80, 300)} className="h-full">
              <Link to={item.href} className="group flex flex-col justify-between h-full bg-card border border-border rounded-lg overflow-hidden mf-card-hover">
                <div>
                  <div className="mf-card-media mf-ratio-card w-full relative mf-img-zoom">
                    <img src={item.image} alt={pick(lang, item.imgAlt)} loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
                  </div>

                  <div className="p-5">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <Badge variant="outline" className="font-mono text-xs font-normal tracking-tight bg-muted text-foreground">
                        {pick(lang, item.badge)}
                      </Badge>
                      <span className="font-mono text-xs text-muted-foreground">
                        {pick(lang, item.specs)}
                      </span>
                    </div>

                    <h3 className="font-semibold text-foreground font-default mb-2 text-lg mf-card-title transition-colors">
                      {pick(lang, item.title)}
                    </h3>

                    <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
                      {pick(lang, item.description)}
                    </p>
                  </div>
                </div>

                <div className="px-5 py-3.5 border-t border-border bg-card flex items-center justify-between text-sm font-medium text-primary">
                  <span>{c.exploreLink}</span>
                  <ArrowRight className="size-4 mf-arrow-nudge text-primary" />
                </div>
              </Link>
            </Reveal>)}
        </div>
      </div>
    </section>;
}